// Detailed case studies for each UX project.
// Used by ProjectDetail.jsx. Keep narrative tight, scannable, and presentable.
//
// ─── How to add showcase images to a case study ───────────────────────
// 1. Drop your images into:  /app/frontend/public/project/inner/
//    (you may also use any web URL).
// 2. Inside the case study's baseSections({ ... }) block, add a `gallery`
//    array. Each entry can be either:
//      • a plain string  →  '/project/inner/my-image.png'
//      • an object       →  { src: '/project/...', caption: 'Hero shot' }
// 3. Images render full-width, stacked, responsive and lazy-loaded.
// Example:
//   gallery: [
//     '/project/inner/spm-hero.png',
//     { src: '/project/inner/spm-flow.png', caption: 'Demand intake flow' },
//   ],
// ──────────────────────────────────────────────────────────────────────

const baseSections = (overrides = {}) => ({
  overview: overrides.overview,
  problem: overrides.problem,
  research: overrides.research,
  insights: overrides.insights || [],
  process: overrides.process || [],
  wireframes: overrides.wireframes || [],
  screens: overrides.screens || [],
  decisions: overrides.decisions || [],
  challenges: overrides.challenges || [],
  outcomes: overrides.outcomes || [],
  learnings: overrides.learnings || [],
  prototypeUrl: overrides.prototypeUrl || '#',
  figmaUrl: overrides.figmaUrl || '#',
  role: overrides.role || 'Lead Product Designer',
  duration: overrides.duration || '6\u201312 weeks',
  team: overrides.team || 'Product, Engineering, QA',
  platform: overrides.platform || 'Web',
  gallery: overrides.gallery || [],
  designSystem: overrides.designSystem || null,   // ← ADD THIS LINE
});

export const caseStudies = {
  u1: {
    id: 'u1', slug: 'spm',
    title: 'SPM \u2014 Strategic Portfolio Management',
    tagline: 'A clearer way for enterprises to plan, prioritise and ship the right work.',
    industry: 'Enterprise SPM', year: 'Uppwise',
    cover: '/project/spm.png',
    ...baseSections({
      overview: 'SPM is Uppwise flagship product — a full Strategy & Portfolio Management platform combining strategy definition, portfolio creation, funding allocation, demand intake, adaptive program management, and resource capacity planning in one unified system. I led the complete UI/UX design of this product, from the initial design proposal through Figma prototyping to engineering handoff and iterative improvement post-launch.',
      problem: 'Organisations needed a product that could close the loop between strategy formulation and portfolio execution in a single, coherent platform. The challenge: no one product in the market did this well without being either too shallow for strategic planning or too complex for operational portfolio management. Uppwise SPM had to be both. The design brief was demanding: a product that C-suite executives would trust for strategic planning, portfolio managers would rely on for day-to-day governance, and program managers would use for execution tracking — all operating on the same data, in the same interface, with role-appropriate views.',
      research: 'Research spanned multiple enterprise engagements and uncovered that the biggest friction point was funding: traditional annual budget cycles were too rigid for the pace of strategic change. Organisations needed a dynamic funding model that allowed investment rebalancing as priorities evolved — and the UX needed to make this feel effortless, not bureaucratic.',
      insights: [
        'Strategy module needed to support multiple strategic frameworks — OKR, Balanced Scorecard, custom frameworks — without locking customers into one approach.',
        'Portfolio optimisation was a black box for most users — the AI-powered optimiser needed a UX that explained its recommendations, not just displayed them.',
        'Demand intake was frequently cited as the "pain point before the pain point" — before projects could be managed, they had to be captured and evaluated, and that process was broken in most organisations.',
        'Resource capacity planning across portfolio scenarios needed to visualise the "so what" — not just show utilisation percentages but flag where the plan was infeasible.',
      ],
      process: [
        { step: 'Discovery', body: 'Multi-enterprise requirement workshops, competitive benchmarking, persona mapping across C-suite through PM.' },
        { step: 'Architecture', body: 'End-to-end IA covering the full strategy-to-execution journey. Modular architecture for feature gating per plan tier.' },
        { step: 'Design system', body: 'Comprehensive SPM design system: full component library, token system, illustration style, icon set, data viz standards.' },
        { step: 'Prototyping', body: 'High-fidelity Figma prototypes across 8 modules. Stakeholder presentations, customer validation sessions.' },
        { step: 'Engineering handoff', body: 'Complete annotated handoff documentation, developer walkthrough sessions, responsive specs, motion guidelines.' },
        { step: 'Iterate', body: 'Usability testing per module, WCAG AA audit, iterative fix cycles, customer sign-off, continuous improvement.' },
      ],
      decisions: [
        'Strategy-First Navigation \u2014 SPM navigation was designed around the strategy-execution journey: Define → Fund → Demand → Execute → Analyse. The top-level navigation encoded the workflow, not just the modules.',
        'Transparent Portfolio Optimiser \u2014 The portfolio optimiser UX was designed to show why a recommendation was made — displaying the scoring criteria, weights, and trade-offs behind each optimised scenario. Explainability was a core design requirement.',
        'Dynamic Funding Rebalancer \u2014 Designed a funding rebalancing interface that visualised investment allocation as an adjustable flow — when one investment increased, the visual feedback showed the constraint impact on others in real time.',
        'Demand Intake Configurator \u2014 The demand intake flow was made fully configurable: custom form fields, workflow routing rules, scoring criteria — all manageable by administrators through a no-code UI builder I designed specifically for this purpose.',
      ],
      challenges: [
        'The Most Complex Product in the Suite \u2014 SPM is the broadest product Uppwise makes. Eight modules, four primary personas, multi-tier feature gating. Maintaining coherence across this complexity was the central design challenge of my work on this platform.',
        'Making AI Recommendations Trustworthy \u2014 The portfolio optimiser algorithmic recommendations were initially distrusted by users who couldnt see why they were made. Redesigning the recommendation UX with transparency and explainability significantly improved adoption.',
        'Admin UX vs User UX \u2014 SPM has a powerful configuration layer for administrators. Designing the admin UX to be powerful without being intimidating — and to stay clearly separated from end-user workflows — required careful IA and careful design language differentiation.',
      ],
      outcomes: [
        'Modules delivered end-to-end',
        'Primary personas served in one product',
        'WCAG AA compliant across entire product',
        'Trust optimiser adoption improved with explainable UX',
      ],
      learnings: [
        'When a product is the most complex thing you have designed, the design system is what keeps you sane — invest in it disproportionately early',
        'AI-powered features require explainability as a UX requirement, not a technical footnote — users need to understand why, not just what.',
        'Journey-based navigation (not module-based) significantly improves discoverability for new users who dont know the products module names yet.',
        'Separating admin and user UX clearly — through distinct design language, not just access controls — prevents end-user confusion and reduces support burden.',
      ],
      duration: '18 months (initial release)', team: 'PM + 2 engineers + Design Lead + 1 designers',

 designSystem: {
  overview:
    'A token-driven, semantic design system built to serve four personas across eight modules — from C-suite strategy to operational execution. Every colour, type, and component decision maps to a named token, so downstream products stay coherent even as the surface area grows.',

  stats: [
    { value: '9',   label: 'Core components' },
    { value: '128', label: 'Component variants' },
    { value: '600+',label: 'System icons' },
    { value: 'AA',  label: 'WCAG compliance' },
  ],

  colorFamilies: [
    {
      name: 'Primary',
      desc: 'Actions, links, active states',
      colors: [
        { name: '50',  hex: '#EFF6FF', role: 'Wash' },
        { name: '100', hex: '#DBE9FF', role: 'Subtle bg' },
        { name: '300', hex: '#7FAAFF', role: 'Focus ring' },
        { name: '500', hex: '#2A78FF', role: 'Hover', contrast: ['AA'] },
        { name: '600', hex: '#0B5FFF', role: 'Default action', contrast: ['AA', 'AAA'] },
        { name: '800', hex: '#0740B5', role: 'Pressed / text' },
      ],
    },
    {
      name: 'Neutral',
      desc: 'Text, surfaces, borders',
      colors: [
        { name: '50',  hex: '#F8FAFC', role: 'Page bg' },
        { name: '100', hex: '#F1F5F9', role: 'Surface' },
        { name: '300', hex: '#CBD5E1', role: 'Border' },
        { name: '500', hex: '#64748B', role: 'Secondary text', contrast: ['AA'] },
        { name: '700', hex: '#334155', role: 'Body text', contrast: ['AAA'] },
        { name: '900', hex: '#0F172A', role: 'Headings', contrast: ['AAA'] },
      ],
    },
    {
      name: 'Semantic',
      desc: 'Status, feedback, data viz',
      colors: [
        { name: 'Success', hex: '#10B981', role: 'On track' },
        { name: 'Warning', hex: '#F59E0B', role: 'At risk' },
        { name: 'Danger',  hex: '#EF4444', role: 'Off track' },
        { name: 'Info',    hex: '#0EA5E9', role: 'Neutral flag' },
        { name: 'Chart-1', hex: '#8B5CF6', role: 'Portfolio A' },
        { name: 'Chart-2', hex: '#EC4899', role: 'Portfolio B' },
      ],
    },
  ],

  typeFamily: 'Inter · JetBrains Mono',
  typography: [
    { name: 'Display / XXL', family: 'Inter, sans-serif', size: '56px', weight: 700, lh: '60px', previewSize: '40px', tracking: '-0.02em', sample: 'Portfolio governance, unified.' },
    { name: 'Display / XL',  family: 'Inter, sans-serif', size: '40px', weight: 600, lh: '48px', previewSize: '32px', sample: 'Strategy to execution' },
    { name: 'Heading / L',   family: 'Inter, sans-serif', size: '28px', weight: 600, lh: '36px', previewSize: '24px', sample: 'Demand intake configurator' },
    { name: 'Heading / M',   family: 'Inter, sans-serif', size: '20px', weight: 600, lh: '28px', sample: 'Resource capacity planning' },
    { name: 'Body / L',      family: 'Inter, sans-serif', size: '16px', weight: 400, lh: '24px', sample: 'Prioritise, plan and deliver initiatives at scale.' },
    { name: 'Body / M',      family: 'Inter, sans-serif', size: '14px', weight: 400, lh: '20px', sample: 'The default reading size across dense data views.' },
    { name: 'Caption',       family: 'Inter, sans-serif', size: '12px', weight: 500, lh: '16px', tracking: '0.06em', sample: 'STATUS · ON TRACK' },
    { name: 'Mono / Data',   family: 'JetBrains Mono, monospace', size: '13px', weight: 500, lh: '20px', sample: 'PRJ-0421  ·  Q3-2025  ·  ₹1.2M' },
  ],

  spacing: [
    { token: 'space-0.5', px: 2 },
    { token: 'space-1',   px: 4 },
    { token: 'space-2',   px: 8 },
    { token: 'space-3',   px: 12 },
    { token: 'space-4',   px: 16 },
    { token: 'space-6',   px: 24 },
    { token: 'space-8',   px: 32 },
    { token: 'space-10',  px: 40 },
    { token: 'space-12',  px: 48 },
    { token: 'space-16',  px: 64 },
  ],

  radii: [
    { token: 'radius-sm', px: 4 },
    { token: 'radius-md', px: 8 },
    { token: 'radius-lg', px: 12 },
    { token: 'radius-xl', px: 20 },
    { token: 'radius-full', px: 999 },
  ],

  elevation: [
    { token: 'elev-0', z: 0, shadow: 'none' },
    { token: 'elev-1', z: 1, shadow: '0 1px 2px rgba(15,23,42,0.06), 0 1px 3px rgba(15,23,42,0.10)' },
    { token: 'elev-2', z: 2, shadow: '0 4px 6px rgba(15,23,42,0.04), 0 10px 15px rgba(15,23,42,0.08)' },
    { token: 'elev-3', z: 3, shadow: '0 10px 15px rgba(15,23,42,0.06), 0 20px 25px rgba(15,23,42,0.10)' },
  ],

  grid: { columns: '12', gutter: '24px', margin: '32px', maxWidth: '1440px' },

  breakpoints: [
    { name: 'sm', min: '≥ 640px'  },
    { name: 'md', min: '≥ 768px'  },
    { name: 'lg', min: '≥ 1024px' },
    { name: 'xl', min: '≥ 1280px' },
    { name: '2xl', min: '≥ 1440px' },
  ],

  motion: [
    { token: 'duration-fast',     value: '120ms' },
    { token: 'duration-base',     value: '200ms' },
    { token: 'duration-slow',     value: '320ms' },
    { token: 'ease-standard',     value: 'cubic-bezier(.2,.0,.0,1)' },
    { token: 'ease-emphasis',     value: 'cubic-bezier(.32,.72,0,1)' },
  ],

  buttonStates: [
    {
      label: 'Primary',
      states: [
        { name: 'Default',  style: { background: '#0B5FFF', color: '#fff' } },
        { name: 'Hover',    style: { background: '#2A78FF', color: '#fff' } },
        { name: 'Pressed',  style: { background: '#0740B5', color: '#fff' } },
        { name: 'Focus',    style: { background: '#0B5FFF', color: '#fff', boxShadow: '0 0 0 3px rgba(11,95,255,0.35)' } },
        { name: 'Disabled', style: { background: 'rgba(11,95,255,0.35)', color: 'rgba(255,255,255,0.6)' } },
      ],
    },
    {
      label: 'Secondary',
      states: [
        { name: 'Default',  style: { background: 'transparent', color: '#0B5FFF', border: '1px solid #0B5FFF' } },
        { name: 'Hover',    style: { background: 'rgba(11,95,255,0.08)', color: '#0B5FFF', border: '1px solid #0B5FFF' } },
        { name: 'Disabled', style: { background: 'transparent', color: 'rgba(148,163,184,0.6)', border: '1px solid rgba(148,163,184,0.3)' } },
      ],
    },
    {
      label: 'Destructive',
      states: [
        { name: 'Default',  style: { background: '#EF4444', color: '#fff' } },
        { name: 'Hover',    style: { background: '#DC2626', color: '#fff' } },
        { name: 'Disabled', style: { background: 'rgba(239,68,68,0.35)', color: 'rgba(255,255,255,0.6)' } },
      ],
    },
  ],

  components: [
    { name: 'Button',        desc: 'Primary, secondary, ghost, destructive with 5 states each.',                 variants: 20, props: ['size', 'variant', 'iconLeft', 'loading', 'disabled'] },
    { name: 'Input & Field', desc: 'Text, select, combobox, date; label, hint, error, success.',                variants: 32, props: ['size', 'state', 'prefix', 'suffix', 'error'] },
    { name: 'Data Table',    desc: 'Sortable, filterable, resizable columns; density + inline edit.',           variants: 12, props: ['density', 'sortable', 'sticky', 'selectable'] },
    { name: 'Card',          desc: 'Portfolio card, initiative card, KPI card, chart card.',                    variants: 9,  props: ['tone', 'elevation', 'interactive'] },
    { name: 'Navigation',    desc: 'Left rail, top bar, breadcrumb, tabs, contextual pane.',                    variants: 14, props: ['collapsed', 'active', 'badge'] },
    { name: 'Modal & Sheet', desc: 'Confirm, form, side sheet; three sizes; scrollable body.',                  variants: 10, props: ['size', 'position', 'dismissable'] },
    { name: 'Feedback',      desc: 'Toast, banner, inline alert — info, success, warning, error.',              variants: 12, props: ['tone', 'action', 'persistent'] },
    { name: 'Charts',        desc: 'Bar, line, gauge, donut, heat-map — token-aware colouring.',                variants: 15, props: ['legend', 'tooltip', 'compact'] },
    { name: 'Iconography',   desc: '600+ system icons on a 24px grid, outline + filled at 4 weights.',          variants: 4,  props: ['weight', 'size'] },
  ],

  principles: [
    { title: 'Clarity first',        desc: 'One decision per screen. Data on request, never in advance.' },
    { title: 'Token-driven',         desc: 'No raw values in components. Every property maps to a semantic token.' },
    { title: 'Composable',           desc: 'Small components with clear responsibilities combine into complex surfaces.' },
    { title: 'Accessible by default',desc: 'WCAG AA is the floor. Colour is never the only signal.' },
    { title: 'Consistent motion',    desc: 'Motion tokens map to intent — enter, exit, emphasis — never decoration.' },
    { title: 'Enterprise-scaled',    desc: 'Density modes, keyboard-first flows and bulk actions are first-class.' },
  ],
},
      
gallery: [
  { src: '/project/inner/spm/1.png',  bg: '#fff' },
  { src: '/project/inner/spm/2.png',  bg: '#fff' },
  { src: '/project/inner/spm/3.png',  bg: '#fff' },
  { src: '/project/inner/spm/4.png',  bg: '#fff' },
  { src: '/project/inner/spm/5.jpg',  bg: '#fff' },
  { src: '/project/inner/spm/6.png',  bg: '#fff' },
  { src: '/project/inner/spm/7.png',  bg: '#fff' },
  { src: '/project/inner/spm/8.png',  bg: '#fff' },
  { src: '/project/inner/spm/9.png',  bg: '#fff' },
  { src: '/project/inner/spm/10.png', bg: '#fff' },
  { src: '/project/inner/spm/11.png', bg: '#fff' },
  { src: '/project/inner/spm/12.png', bg: '#fff' },
  { src: '/project/inner/spm/13.png', bg: '#fff' },
  { src: '/project/inner/spm/14.png', bg: '#fff' },
],

      figmaUrl: 'https://www.figma.com/design/Xd6F1VN3AufM84ejUq96LN/Netsle-New-Design-React-App--1-?node-id=3582-1106&t=pFAEJSefOR33O0ZR-1',
    }),
  },
  u2: {
    id: 'u2', slug: 'iconography',
    title: 'Iconography \u2014 600+ system icons',
    tagline: 'A unified visual vocabulary for a multi-product suite.',
    industry: 'System', year: 'Iconography',
    cover: '/project/icons.png',
    ...baseSections({

      overview: 'A ground-up icon system for the Project Objects suite \u2014 600+ icons designed from a single grid, with a strict vocabulary and explicit usage guidance.',
      problem: 'The suite shipped with mixed-source icons \u2014 inconsistent strokes, optical weights and semantics. Customers complained about a \u201cstitched together\u201d feel.',
      research: 'Audit of existing icon usage across 4 products, semantic clustering, and a literature review of leading icon systems (Material, Carbon, Polaris).',
      insights: [
        'Most icon misuse came from missing alternates, not designer carelessness.',
        'Stroke weight should change with grid size \u2014 not just scale.',
        'Filled vs. outline must follow a single rule across the system.',
      ],
      process: [
        { step: 'Foundations', body: 'Defined the grid (24px / 16px), corner radius, terminal style and optical sizing rules.' },
        { step: 'Vocabulary', body: 'Mapped 600 concepts onto a controlled vocabulary of metaphors.' },
        { step: 'Drawing', body: 'Drew each icon twice \u2014 outline + filled \u2014 against the master grid.' },
        { step: 'Tooling', body: 'Authored a Figma library with shared properties and an SVG export pipeline.' },
        { step: 'Docs', body: 'Wrote usage guidance with do\u2019s and don\u2019ts for every concept family.' },
      ],
      decisions: [
        'One stroke weight per grid, no exceptions \u2014 even for visual hacks.',
        'Brand icons live outside the system to keep semantics clean.',
        'No mid-action icons (e.g. \u201cpaused\u201d as a third state) \u2014 motion handles state.',
      ],
      challenges: [
        'Maintaining optical balance across 24px and 16px in a single source file.',
        'Versioning 600 icons through three product redesigns.',
      ],
      outcomes: [
        'Adopted across 4 products with zero brand-team escalations in 12 months.',
        'Estimated 35% reduction in icon-related design tickets.',
      ],
      learnings: [
        'A system is only as good as its docs. Drawings are a third of the work.',
      ],
      duration: '5 months', team: 'Solo design with engineering review',
      gallery: [
        // '/project/inner/icons/icons.png',
   ['/project/inner/icons/1.png',  '/project/inner/icons/2.png'],             // 2 col row
  ['/project/inner/icons/3.png',  '/project/inner/icons/4.png'],
  ['/project/inner/icons/5.png',  '/project/inner/icons/6.png'],
  ['/project/inner/icons/7.png',  '/project/inner/icons/8.png'],
  ['/project/inner/icons/9.png',  '/project/inner/icons/10.png'],
  ['/project/inner/icons/11.png', '/project/inner/icons/12.png'],
  ['/project/inner/icons/13.png', '/project/inner/icons/14.png'],

      ],
      figmaUrl: 'https://www.figma.com/design/NKl7klpcv7f5Jr1rEC4oAH/Icon-AR--For-General-Use-?node-id=0-1&p=f&t=0sodZY8tvUlw13bF-0', 
    }),
  },
  u3: {
    id: 'u3', slug: 'vitasync',
    title: 'VitaSync \u2014 Mind, Body & Balance',
    tagline: 'A daily wellness companion that respects your time and attention.',
    industry: 'Wellness', year: 'R&D',
    cover: '/project/vita.png',
    ...baseSections({
            overview:
        'VitaSync is a mobile health and wellness application designed to help users monitor their health metrics, track daily activities, and gain meaningful insights into their overall well-being. The goal of the product was to create a seamless and engaging experience that makes health tracking less intimidating and more actionable. By combining personalized onboarding, intuitive dashboards, and easy-to-understand visualizations, VitaSync empowers users to stay informed about their health and build healthier habits over time.',

      problem:
        'Many health and fitness applications provide users with an overwhelming amount of information, making it difficult to understand what truly matters. Users often struggle to interpret health metrics, stay motivated to track their progress consistently, and navigate through complex interfaces. Additionally, lengthy onboarding processes and cluttered dashboards lead to poor engagement and high drop-off rates. The challenge was to design a user-friendly health tracking experience that simplifies complex health data, encourages regular engagement, and helps users make informed decisions about their well-being.',

      research:
        'To better understand user needs and industry standards, I ran a competitive analysis of leading health and fitness applications, reviewed user feedback across stores and communities, and analysed common behavioural patterns in wellness products. Research objectives: understand how users currently track their health, identify pain points in existing apps, discover opportunities to improve engagement, and explore effective ways of presenting health data.',

      insights: [
        'Users prefer quick summaries over detailed health reports.',
        'Complex dashboards often discourage regular usage.',
        'Personalised experiences increase engagement and retention.',
        'Visual progress indicators help users stay motivated.',
        'Simplified onboarding significantly improves the first-time user experience.',
      ],

      decisions: [
        'Clarity over data — surface "how am I doing and what should I do next", not raw numbers. Long-form reports were deprioritised in favour of glanceable summaries.',
        'Progress as the motivator — every key metric pairs with a visual progress indicator (rings, bars, streak-free goal cards) so improvement is felt, not calculated.',
        'Simplicity builds trust — restrained typography, generous spacing and a single primary action per screen made users feel confident interpreting their own data.',
        'Personalisation matters — recommendations and goals adapt to the user\u2019s age, activity level, and stated lifestyle from the onboarding answers, not generic averages.',
      ],

      process: [
        { step: 'Discovery & Analysis', body: 'Mapped user needs, ran a competitive teardown of leading wellness apps, and translated pain points into clear design opportunities.' },
        { step: 'Information Architecture', body: 'Organised the app into six clear sections — Onboarding, Authentication, Dashboard, Health Details, Notifications, Profile — so the most important information is always one tap away.' },
        { step: 'User Flows & Wireframes', body: 'Mapped journeys for onboarding, profile setup, health monitoring and progress tracking. Low-fidelity wireframes validated hierarchy and navigation before any pixels.' },
        { step: 'Visual Design', body: 'A clean, modern visual language with a wellness-focused palette. Health data is presented as cards, circular progress indicators and concise summaries for instant comprehension.' },
        { step: 'Prototyping & Iteration', body: 'Interactive prototypes were tested and refined to streamline workflows and tighten consistency. Multiple iterations were driven by review and testing feedback.' },
      ],

      outcomes: [
        'Simplified onboarding reduced first-time user friction.',
        'Health metrics became easier to understand through visual representations.',
        'Dashboard surfaces the most important information at a glance.',
        'Consistent design patterns improved overall usability across the app.',
        'Personalised goal tracking encouraged continued engagement.',
      ],
     
      learnings: [
        'In wellness, less data shown clearly always beats more data shown completely.',
        'Onboarding is the product\u2019s real first impression — design it before the dashboard.',
        'Visual progress is a stronger motivator than streak guilt, especially in long-term health behaviour.',
      ],

gallery: [
  [
    { src: '/project/inner/vita/1.png',  maxWidth: 200 },
    { src: '/project/inner/vita/2.png',  maxWidth: 200 },
    { src: '/project/inner/vita/3.png',  maxWidth: 200 },
    { src: '/project/inner/vita/4.png',  maxWidth: 200 },
  ],
  [
    { src: '/project/inner/vita/5.png',  maxWidth: 200 },
    { src: '/project/inner/vita/6.png',  maxWidth: 200 },
    { src: '/project/inner/vita/7.png',  maxWidth: 200 },
    { src: '/project/inner/vita/8.png',  maxWidth: 200 },
  ],
  [
    { src: '/project/inner/vita/9.png',  maxWidth: 200 },
    { src: '/project/inner/vita/10.png', maxWidth: 200 },
    { src: '/project/inner/vita/11.png', maxWidth: 200 },
    { src: '/project/inner/vita/12.png', maxWidth: 200 },
  ],
  [
    { src: '/project/inner/vita/13.png', maxWidth: 200 },
    { src: '/project/inner/vita/14.png', maxWidth: 200 },
    { src: '/project/inner/vita/15.png', maxWidth: 200 },
    { src: '/project/inner/vita/16.png', maxWidth: 200 },
  ],
],
figmaUrl: 'https://www.figma.com/design/yeMQMrSZZ3EYPNQRuOsMBD/VitaSync--1-?node-id=26-4462&p=f&t=0sodZY8tvUlw13bF-0', 
    }),
  },
  u4: {
    id: 'u4', slug: 'dmart-comfort',
    title: 'DMart Comfort',
    tagline: 'Pay before you reach the counter \u2014 skip the queue.',
    industry: 'Retail', year: 'R&D',
    cover: '/project/d-mart.png',
    ...baseSections({
            overview:
        'DMart is a mobile-first grocery shopping experience designed to simplify the way users discover, purchase, and manage their daily household essentials. The project aimed to bridge the gap between traditional grocery shopping and modern digital convenience by creating a seamless shopping journey that helps users browse products, manage shopping lists, track orders, and complete purchases with minimal effort. The primary focus was to design an intuitive experience that caters to a wide range of users — from tech-savvy shoppers to first-time digital grocery buyers. The solution prioritises speed, accessibility, and convenience while maintaining the familiarity users associate with DMart\u2019s retail experience.',

      problem:
        'Despite the growing adoption of online grocery shopping, many users still face challenges when purchasing everyday essentials through digital platforms. Existing solutions often suffer from complex navigation, cluttered product listings, lengthy checkout processes, and poor product discoverability. Key challenges identified were: difficulty finding products quickly among large inventories, inefficient experiences for repeat purchases, lack of personalised recommendations, complicated checkout flows leading to cart abandonment, and limited visibility into order status and delivery updates. The challenge was to create a user-centred shopping experience that reduces friction throughout the purchase journey while making grocery shopping faster, more convenient, and more enjoyable.',

      research:
        'To understand user expectations and behaviours, a combination of competitive analysis, market research, and user journey mapping was conducted. Research objectives: understand how users shop for groceries online, identify pain points across the shopping journey, analyse competitors in the grocery and e-commerce space, and discover opportunities to improve product discovery and checkout experiences.',

      insights: [
        'Users prefer speed over exploration — most arrive with a predefined list and want to finish fast.',
        'Search beats browse for recurring household items; product search is critical.',
        'Repeat purchases drive engagement — easy access to past orders is a habit-builder.',
        'Multi-step checkout causes drop-offs, especially on mobile.',
        'Trust signals (transparent pricing, delivery info, clear product details) drive the final decision.',
      ],

      decisions: [
        'Convenience is the primary motivation — every flow was optimised to remove a step rather than add a feature.',
        'Familiarity builds confidence — interface patterns mirror real-world retail behaviour to lower the learning curve for first-time digital shoppers.',
        'Personalisation improves retention — relevant recommendations and one-tap reorder for previously purchased items.',
        'Visual hierarchy drives decision-making — product cards lead with price, discount, rating and availability, in that order.',
        'One-handed mobile use — primary actions sit in the bottom 60% of the screen so the shopping happens on the go, with one thumb.',
      ],

      process: [
        { step: 'Discovery & Requirements', body: 'Mapped business objectives, user needs, and market opportunities. Analysed existing grocery experiences to identify strengths, weaknesses, and opportunities for innovation.' },
        { step: 'User Flows & IA', body: 'Simplified information architecture around six core actions: Browse Products, Search Products, Add to Cart, Checkout, Track Orders, Manage Profile — reducing cognitive load and improving navigation efficiency.' },
        { step: 'Wireframing', body: 'Low-fi wireframes for content hierarchy and interaction patterns, with deliberate focus on product discoverability, search experience, cart management, and the checkout flow. Multiple iterations explored to optimise task completion.' },
        { step: 'Visual Design System', body: 'Scalable system covering product cards, search components, category navigation, cart elements, checkout modules, and form controls. Visual identity aligned with the DMart brand while keeping the digital experience clean and modern.' },
        { step: 'Hi-fi Design & Prototyping', body: 'Final interface focused on clarity, accessibility, and usability. Interactive prototypes validated user journeys and refined interactions before delivery.' },
      ],

      outcomes: [
        'Simplified navigation improved product discoverability.',
        'Faster checkout flow reduced purchasing friction.',
        'Personalised experience increased convenience for repeat shoppers.',
        'Consistent design system improved usability across screens.',
        'Clear information hierarchy enhanced decision-making at the product card level.',
        'Mobile-first approach created a more accessible shopping experience overall.',
      ],

      challenges: [
        'Managing a Large Product Catalog — users struggled to find products among thousands of items; deep category browsing slowed everything down.',
        'Reducing Checkout Abandonment — a lengthy checkout and excessive form filling discouraged completion, especially on mobile.',
        'Improving Product Discoverability — relevant products, offers, and alternatives were getting missed due to information overload and weak content organisation.',
        'Supporting Different User Types — the same product had to onboard first-time digital grocery buyers and feel instant for returning power users.',
        'Balancing Features with Simplicity — integrating search, categories, offers, cart, payments, and order tracking without overwhelming the screen was a constant tension.',
        'Building User Trust — users needed assurance on product quality, pricing transparency, payment security, and delivery reliability before placing orders.',
      ],

      decisions: [
        'Search-first navigation — instead of forcing users through deep category trees, a prominent search experience was placed at the top of every primary surface to help users get to a product in two taps.',
        'Simplified checkout — reduced steps, removed non-essential fields, and surfaced pricing, delivery info, and order summary on a single page to cut abandonment.',
        'Enhanced product visibility — product cards lead with image, price, discount, rating, and availability so a decision can be made in a single glance.',
        'Personalised shopping journey — recently purchased items, saved preferences, and tailored recommendations bring repeat shoppers to checkout in under a minute.',
        'Clear information hierarchy — content and actions are prioritised by frequency of use, so the most-used actions are always within thumb reach.',
        'Transparency and trust by default — clear product information, live order tracking, secure-payment indicators, and proactive delivery updates are present throughout the flow.',
        'Consistent design system — reusable components and standardised patterns keep the visual language coherent and the build scalable.',
        'Mobile-first interactions — interface optimised for one-handed use and quick task completion, since most grocery shopping happens on the go.',
      ],

      learnings: [
        'In grocery, every removed step beats every added feature — speed is the value proposition.',
        'Replicating familiar retail patterns lowers the activation barrier for first-time digital grocery users.',
        'Reorder lists are the most underrated retention surface in commerce apps — they pay back forever.',
        'Convenience is the primary motivation — users come to save time, not to explore.',
        'Personalisation drives retention — relevant recommendations and one-tap reorder transform first-time buyers into habitual users.',
        'Visual hierarchy on the product card decides the sale — price, discount, rating, availability, in that order.',
      ],

      role: 'Lead Product Designer',
      duration: '6 weeks',
      team: 'Solo design with engineering review',
      platform: 'iOS & Android',
gallery: [
  [
    { src: '/project/inner/d-mart/1.png',  maxWidth: 200 },
    { src: '/project/inner/d-mart/2.png',  maxWidth: 200 },
    { src: '/project/inner/d-mart/3.png',  maxWidth: 200 },
    { src: '/project/inner/d-mart/4.png',  maxWidth: 200 },
  ],
  [
    { src: '/project/inner/d-mart/5.png',  maxWidth: 200 },
    { src: '/project/inner/d-mart/6.png',  maxWidth: 200 },
    { src: '/project/inner/d-mart/7.png',  maxWidth: 200 },
    { src: '/project/inner/d-mart/8.png',  maxWidth: 200 },
  ],
  [
    { src: '/project/inner/d-mart/9.png',  maxWidth: 200 },
    { src: '/project/inner/d-mart/10.png', maxWidth: 200 },
    { src: '/project/inner/d-mart/11.png', maxWidth: 200 },
    { src: '/project/inner/d-mart/12.png', maxWidth: 200 },
  ],
  [
    { src: '/project/inner/d-mart/13.png', maxWidth: 200 },
    { src: '/project/inner/d-mart/14.png', maxWidth: 200 },
    { src: '/project/inner/d-mart/15.png', maxWidth: 200 },
    { src: '/project/inner/d-mart/16.png', maxWidth: 200 },
  ],
  [
    { src: '/project/inner/d-mart/17.png', maxWidth: 200 },
    { src: '/project/inner/d-mart/18.png', maxWidth: 200 },
    { src: '/project/inner/d-mart/19.png', maxWidth: 200 },
    { src: '/project/inner/d-mart/20.png', maxWidth: 200 },
  ],
],

figmaUrl: 'https://www.figma.com/file/JZBwXqprlQhmduQ4xivGQV/D-Mart---Comfort---Assignment?type=design&node-id=4%3A198&t=jLqvr9SmzqVmIhqT-1', 
    }),
  },
  u5: {
    id: 'u5', slug: 'cocobite',
    title: 'CocoBite',
    tagline: 'A food delivery app for people who order the same thing every Tuesday.',
    industry: 'Food', year: 'R&D',
    cover: '/project/coco.png',
    ...baseSections({
           overview:
        'CocoBite is a mobile food delivery application designed to connect users with their favourite restaurants through a fast, convenient, and visually engaging ordering experience. The app allows users to browse menus, discover popular dishes, place orders, track deliveries, and enjoy a seamless ordering journey from start to finish. The primary goal of the project was to create a user-friendly food delivery platform that simplifies decision-making, reduces ordering friction, and enhances the dining experience for busy users who rely on mobile apps for everyday food needs.',

      problem:
        'The online food delivery market is highly competitive, yet many applications still struggle with usability challenges that hurt satisfaction and conversion. Common issues identified: difficulty discovering relevant restaurants and dishes, information-heavy interfaces that overwhelm users, complicated ordering and checkout, poor visibility of offers, ratings and delivery info, and a lack of personalisation. The challenge was to design a food delivery experience that lets users find, select, and order food quickly while staying visually appealing and intuitive.',

      research:
        'To understand user expectations and food ordering behaviours, a competitive analysis and user behaviour study were conducted across popular food delivery platforms. Research objectives: understand how users search for food online, identify common frustrations during ordering, analyse interaction patterns used by leading competitors, and discover opportunities to improve the ordering experience.',

      insights: [
        'Users make quick decisions — most spend very little time browsing and want clear information.',
        'Visual content influences purchases — food images significantly drive engagement and orders.',
        'Convenience is the top priority — speed, simplicity, and minimal effort throughout the flow.',
        'Offers drive engagement — discounts, promotions, and recommended items lift frequency.',
        'Trust matters — ratings, reviews, ETAs, and restaurant information shape confidence.',
      ],

      challenges: [
        'Simplifying Food Discovery — letting users browse restaurants and discover meals without feeling overwhelmed by choice.',
        'Reducing Decision Fatigue — large menus and endless options were delaying purchase decisions.',
        'Creating a Seamless Ordering Flow — keeping the order journey fast and intuitive while minimising drop-offs.',
        'Balancing Information and Visual Appeal — surfacing important details without compromising on aesthetics.',
        'Building Trust in Restaurant Selection — giving users enough info to confidently pick restaurants and dishes.',
        'Maintaining Consistency Across Screens — a cohesive experience across browsing, ordering, and checkout.',
      ],

      decisions: [
        'Search and discovery first — restaurant discovery and menu browsing were treated as primary actions, getting users to the desired item in two taps.',
        'Strong visual hierarchy — large food imagery and clear content organisation support faster, more confident decisions.',
        'Streamlined ordering journey — the checkout was simplified to reduce friction and improve completion rates.',
        'Highlighted ratings and delivery info — trust indicators (ratings, reviews, ETAs) sit prominently on every key surface.',
        'Personalised recommendations — popular dishes, featured restaurants and suggested items boost engagement and order value.',
        'Consistent design system — reusable components and visual patterns maintain consistency and scalability.',
        'Mobile-first experience — interactions optimised for one-handed use and quick task completion.',
      ],

      process: [
        { step: 'Discovery & Requirements', body: 'Mapped user needs, business objectives, and market expectations. Competitor teardowns identified strengths and gaps in existing food delivery platforms.' },
        { step: 'User Flow Mapping', body: 'Mapped the full ordering journey from restaurant discovery to order confirmation to surface and remove unnecessary friction.' },
        { step: 'Wireframing', body: 'Low-fi wireframes established content hierarchy, screen layouts, and interaction patterns before any visual design.' },
        { step: 'Visual Design & Branding', body: 'A dark-themed visual identity with warm accent colours created a premium, appetising tone — emphasising food imagery without losing readability.' },
        { step: 'Design System', body: 'Scalable system covering buttons, cards, navigation, input fields, product listings, and checkout components — keeping the build consistent and future-friendly.' },
        { step: 'Hi-fi Prototyping', body: 'Interactive prototypes validated user flows, refined micro-interactions, and tightened usability before handoff.' },
      ],

      outcomes: [
        'Improved restaurant and food discoverability.',
        'Faster, more intuitive ordering experience.',
        'Simplified checkout process and higher completion rates.',
        'Stronger engagement driven by visual presentation.',
        'Better trust through transparent ratings and delivery information.',
        'Consistent, scalable design system across the app.',
        'Mobile-first experience optimised for convenience and efficiency.',
      ],

      learnings: [
        'Users prefer simplicity over complexity — every removed screen pays dividends in conversion.',
        'Food imagery creates emotional engagement — visuals do half the selling on a food app.',
        'Personalised recommendations cut decision fatigue and bring repeat orders to checkout faster.',
        'Mobile users demand speed — most orders happen while multitasking, and the UI has to keep up.',
        'Transparency builds trust — clear pricing, ETAs, ratings, and live tracking decide whether they come back.',
      ],

       role: 'Lead Product Designer',
      duration: '6 weeks',
      team: 'Solo design with engineering review',
      platform: 'iOS & Android',

      gallery: [
  [
    { src: '/project/inner/coco/1.png',  maxWidth: 200 },
    { src: '/project/inner/coco/2.png',  maxWidth: 200 },
    { src: '/project/inner/coco/3.png',  maxWidth: 200 },
  ], 
],

figmaUrl: 'https://www.figma.com/design/VxG82CKIeDsbWwnKeWCDiv/CocoBite?node-id=0-1&p=f&t=0sodZY8tvUlw13bF-0',

    }),
  },
  u6: {
    id: 'u6', slug: 'kitchenrush',
    title: 'KitchenRush',
    tagline: 'Cloud-kitchen-first food delivery.',
    industry: 'Food', year: 'R&D',
    cover: '/project/kitchenrush.png',
    ...baseSections({
      overview:
        'KitchenRush is a mobile food delivery application designed to provide users with a fast, convenient, and reliable way to order food from nearby restaurants. The platform focuses on simplifying the food ordering journey by enabling users to discover restaurants, browse menus, place orders, and track deliveries in real time. The project aimed to create an intuitive and engaging user experience that reduces ordering friction, improves customer satisfaction, and helps users receive their favourite meals quickly — with simplicity, accessibility, and efficiency at every step.',

      problem:
        'The increasing demand for online food delivery has created opportunities for digital platforms, but many existing applications still struggle with usability issues that affect customer satisfaction. Users often experience difficulty finding restaurants and meals quickly, complicated onboarding and registration, lack of transparency during order tracking, long ordering flows that increase drop-offs, and poor navigation between menus, cart, and checkout. The challenge was to design a food delivery experience that lets users order meals effortlessly while maintaining clarity, speed, and trust throughout the journey.',

      research:
        'Research was conducted through competitor analysis, market observation, and user behaviour studies. Quantitative findings highlighted the gaps clearly: 80% of users were dissatisfied with delayed and inconsistent delivery estimates, 50% preferred ordering through mobile apps over traditional methods, 30% reported difficulty discovering restaurants or meals that matched their preferences, 90% considered real-time delivery tracking an essential feature, and 60% wanted a faster, more streamlined ordering experience. Together these reinforced one thing: speed, convenience, and transparency are the experience.',

      insights: [
        '80% of users were dissatisfied with delayed deliveries and inconsistent ETAs.',
        '50% preferred mobile apps over traditional ordering methods.',
        '30% reported difficulty discovering restaurants or meals matching their preferences.',
        '90% considered real-time delivery tracking an essential feature.',
        '60% wanted a faster and more streamlined ordering experience.',
      ],


      challenges: [
        'Simplifying the Onboarding Process — new users needed a quick, frictionless way into the app without feeling overwhelmed.',
        'Improving Food Discovery — letting users browse restaurants and discover meals based on their preferences with minimal effort.',
        'Reducing Checkout Complexity — fewer steps in ordering and payment to avoid discouraging completion.',
        'Providing Transparent Delivery Tracking — clear visibility into order progress after checkout.',
        'Balancing Information and Simplicity — presenting menus, pricing, restaurant details and delivery info without cluttering the UI.',
        'Supporting Different User Types — accommodating both first-time users and returning customers with different needs and behaviours.',
      ],

      decisions: [
        'Simple onboarding flow — minimal screens that communicate the app\u2019s value and get users to their first order quickly.',
        'Discovery-first home — restaurant listings, categories, and food recommendations structured to reduce search effort and improve discoverability.',
        'Streamlined ordering journey — number of steps to place an order minimised to lift completion rates and reduce drop-offs.',
        'Real-time order tracking — a dedicated tracking surface provides live visibility into delivery progress, building trust at the moment users are most anxious.',
        'Mobile-first interactions — interface optimised for one-handed use and quick navigation, since the primary audience orders on the move.',
        'Consistent visual system — reusable components and consistent interaction patterns keep the experience cohesive across every screen.',
      ],

      process: [
        { step: 'Discover', body: 'Mapped business objectives, market opportunities, and user expectations. Analysed existing food delivery platforms to identify usability gaps and opportunities.' },
        { step: 'Define', body: 'Synthesised research findings into user pain points, business goals, and design requirements. Established core user journeys and product objectives.' },
        { step: 'Ideate', body: 'Brainstorming sessions, task-flow mapping, and user journey creation. Multiple concepts were evaluated to find the most effective experience.' },
        { step: 'Design', body: 'Wireframes, visual exploration, design system creation, and hi-fi prototypes. Two anchor flows were validated end-to-end: Registration (Splash → Onboarding → Login → Home) and Order (Home → Browse → Meal Details → Cart → Place Order → Confirmation).' },
        { step: 'Design System', body: 'A consistent visual language with a primary green palette, clean white backgrounds, Poppins typography, and reusable buttons, cards and navigation components — scalable across the product.' },
        { step: 'Prototype & Validate', body: 'Hi-fi screens turned into interactive prototypes for usability testing and iterative refinement before handoff.' },
      ],

      outcomes: [
        'Simplified onboarding increased accessibility for new users.',
        'Improved restaurant and food discoverability.',
        'Reduced friction during ordering and checkout.',
        'Enhanced trust through real-time order tracking.',
        'Created a consistent and scalable design system.',
        'Delivered a mobile-first experience optimised for everyday use.',
      ],

      learnings: [
        'Convenience is the main motivation — users come to save time, not to explore.',
        'Real-time tracking is a trust feature, not a tracking feature — the visibility itself is the value.',
        'Fast ordering matters most during busy schedules — speed beats personalisation at the moment of intent.',
        'Visual content is decision-making fuel — food imagery materially lifts conversion.',
        'Simplicity drives retention — a clean, intuitive interface is what brings users back tomorrow.',
      ],

            role: 'Lead Product Designer',
      duration: '7 weeks',
      team: 'Solo design with engineering review',
      platform: 'iOS & Android',
gallery: [

  [
    { src: '/project/inner/kitchen/1.png',  maxWidth: 200 },
    { src: '/project/inner/kitchen/2.png',  maxWidth: 200 },
    { src: '/project/inner/kitchen/3.png',  maxWidth: 200 },
    { src: '/project/inner/kitchen/4.png',  maxWidth: 200 },
  ],
  [
    { src: '/project/inner/kitchen/5.png',  maxWidth: 200 },
    { src: '/project/inner/kitchen/6.png',  maxWidth: 200 },
    { src: '/project/inner/kitchen/7.png',  maxWidth: 200 },
    { src: '/project/inner/kitchen/8.png',  maxWidth: 200 },
  ],
  [
    { src: '/project/inner/kitchen/9.png',  maxWidth: 200 },
    { src: '/project/inner/kitchen/10.png', maxWidth: 200 },
    { src: '/project/inner/kitchen/11.png', maxWidth: 200 },
    { src: '/project/inner/kitchen/12.png', maxWidth: 200 },
    { src: '/project/inner/kitchen/13.png', maxWidth: 200 },
  ],

],

figmaUrl: 'https://www.figma.com/design/waNmdYOQEw2JDPGJ5Vzo2A/KitchenRush-App?node-id=0-1&p=f&t=0sodZY8tvUlw13bF-0',

    }),
  },
  u7: {
    id: 'u7', slug: 'smartbus',
    title: 'SmartBus',
    tagline: 'Live tracking and ticketing for public bus transit.',
    industry: 'Transit', year: 'R&D',
    cover: '/project/smartbus.png',
    ...baseSections({
      overview:
        'Smart Bus Tracker is a mobile application designed to improve the public transportation experience by helping users track buses in real time, plan journeys efficiently, and access important transit information from a single platform. The project was focused on solving common commuting challenges such as uncertain arrival times, lack of route visibility, and inefficient communication between passengers and transit services. The goal was to create a reliable, easy-to-use mobile experience that makes daily commuting more predictable and stress-free.',

      problem:
        'Public transportation users often face uncertainty when planning their journeys. Limited visibility into bus locations, inaccurate arrival estimates, and poor access to route information lead to missed buses, longer wait times, and frustrating travel experiences. Key challenges identified: lack of real-time bus tracking, difficulty planning routes efficiently, limited visibility into bus schedules and stops, poor communication regarding delays and service updates, and transit information scattered across multiple sources. The challenge was to design a centralised and user-friendly platform that provides commuters with accurate, real-time information while simplifying trip planning and tracking.',

      research:
        'Research was conducted through competitor analysis, user interviews, and transit-service reviews. Research objectives: understand daily commuting behaviours, identify pain points in public transportation systems, analyse existing transit and tracking applications, and discover opportunities to improve travel planning and tracking experiences.',

      insights: [
        'Real-time information is critical — accurate arrival times and live tracking drive trip planning.',
        'Route planning creates friction — users struggle to identify the fastest, most convenient option.',
        'Delays cause frustration — unexpected delays and lack of communication tank satisfaction.',
        'Mobile is the primary platform — commuters access transit info on the move, on their phones.',
        'Simplicity improves adoption — quick access beats complex menus every time.',
      ],

      challenges: [
        'Presenting Complex Transit Data — routes, schedules, stops, and vehicle locations can easily overwhelm.',
        'Ensuring Easy Route Discovery — a simple way to find and compare travel options.',
        'Displaying Real-Time Information Clearly — live tracking must be accurate, understandable, and visually accessible.',
        'Supporting Different Commuter Needs — regular commuters and occasional travellers expect different things.',
        'Reducing Information Overload — organising large amounts of transit info without cluttering the UI.',
        'Creating Trust in Arrival Predictions — users need confidence that ETAs are reliable.',
      ],
      decisions: [
        'Real-time tracking as the hero — live bus tracking is the primary feature, with vehicle locations and ETAs visible at a glance.',
        'Simplified route planning — quickly identify suitable travel options without unnecessary complexity.',
        'Quick-access information — tracking, schedules, and route search live in the most reachable parts of the navigation.',
        'Visual status indicators — clear progress visuals communicate bus movement and service updates instantly.',
        'Smart notifications — arrival alerts, delay notifications, and route updates keep users informed throughout the journey.',
        'Consistent design system — reusable components and standardised patterns ensure a cohesive experience across screens.',
      ],

      process: [
        { step: 'User Research & Discovery', body: 'Understood commuter behaviours, transportation challenges, and stakeholder requirements. Surfaced and prioritised user pain points.' },
        { step: 'Requirement Definition', body: 'Translated insights into product goals and key functional requirements — focused on tracking, route planning, and schedule management.' },
        { step: 'Competitive Analysis', body: 'Evaluated existing transit and mobility apps to identify strengths, weaknesses, and opportunities for improvement.' },
        { step: 'User Flows', body: 'Mapped key journeys — Searching Routes, Tracking Buses, Viewing Schedules, Managing Trips, Receiving Notifications — to validate navigation and feature access.' },
        { step: 'Information Architecture', body: 'Organised the app structure so users move easily between routes, schedules, tracking, and account features.' },
        { step: 'Wireframing', body: 'Low-fidelity wireframes established layouts, content hierarchy, and screen relationships before visual design.' },
        { step: 'Design System', body: 'Scalable system covering buttons, navigation, cards, status indicators, form elements, and route-information components — with defined typography, iconography, and colour guidelines.' },
        { step: 'High-Fidelity UI', body: 'Bold visual identity aligned with transportation branding while staying clear and accessible.' },
        { step: 'Prototyping & Iteration', body: 'Interactive prototypes were tested and refined to improve usability, navigation efficiency, and overall satisfaction.' },
      ],
      outcomes: [
        'Improved access to real-time bus tracking.',
        'Simplified route planning and trip management.',
        'Reduced uncertainty during daily commutes.',
        'Enhanced visibility of schedules and transit information.',
        'Established a scalable and consistent design system.',
        'Delivered a mobile-first experience optimised for on-the-go usage.',
      ],
 learnings: [
        'Users want predictability — accurate arrival info is the single biggest unlock in public transit UX.',
        'Time is the most important factor — every feature is judged by how much waiting it removes.',
        'Real-time tracking builds confidence — visibility is the trust mechanism, not the tracking itself.',
        'Navigation should be effortless — schedules, routes, and tracking should be reachable in two taps.',
        'Notifications add value — timely alerts about delays and arrivals turn the app into a daily habit.',
      ],
      
      role: 'Lead Product Designer',
      duration: '8 weeks',
      team: 'Solo design with engineering review',
      platform: 'iOS & Android',

      gallery: [

  [
    { src: '/project/inner/smartbus/1.png',  maxWidth: 200 },
    { src: '/project/inner/smartbus/2.png',  maxWidth: 200 },
    { src: '/project/inner/smartbus/3.png',  maxWidth: 200 },
    { src: '/project/inner/smartbus/4.png',  maxWidth: 200 },
  ],
  [
    { src: '/project/inner/smartbus/5.png',  maxWidth: 200 },
    { src: '/project/inner/smartbus/6.png',  maxWidth: 200 },
    { src: '/project/inner/smartbus/7.png',  maxWidth: 200 },
    { src: '/project/inner/smartbus/8.png',  maxWidth: 200 },
  ],
  [
    { src: '/project/inner/smartbus/9.png',  maxWidth: 200 },
    { src: '/project/inner/smartbus/10.png', maxWidth: 200 },
    { src: '/project/inner/smartbus/11.png', maxWidth: 200 },
    { src: '/project/inner/smartbus/12.png', maxWidth: 200 },
    { src: '/project/inner/smartbus/13.png', maxWidth: 200 },
  ],
   [
    { src: '/project/inner/smartbus/14.png',  maxWidth: 200 },
    { src: '/project/inner/smartbus/15.png', maxWidth: 200 },
    { src: '/project/inner/smartbus/16.png', maxWidth: 200 },
    { src: '/project/inner/smartbus/17.png', maxWidth: 200 },
    { src: '/project/inner/smartbus/18.png', maxWidth: 200 },
    { src: '/project/inner/smartbus/19.png', maxWidth: 200 },
  ],

],

      figmaUrl: 'https://www.figma.com/design/DQ0F8SbhiZgU3qFQQsYvYq/SmartBus-App?node-id=2042-25&t=RPxI479AfjldGkXv-0',

    }),
  },
  u8: {
    id: 'u8', slug: 'rydigo',
    title: 'Rydigo',
    tagline: 'A digital driver solution \u2014 documents, earnings and routes in one place.',
    industry: 'Mobility', year: 'R&D',
    cover: '/project/rydigo.png',
    ...baseSections({
      overview:
        'Rydigo is a ride-booking mobile application designed to provide users with a convenient, reliable, and secure transportation experience. The platform enables users to book rides, track drivers in real time, choose vehicle options, and manage their journeys through a simple and intuitive interface. The project focused on creating a seamless mobility solution that simplifies the ride-booking process while ensuring transparency, safety, and efficiency for both riders and drivers — designed to reduce booking friction and give users greater confidence throughout their journey.',

      problem:
        'Traditional transportation services often create challenges for users due to inconsistent availability, lack of real-time visibility, and inefficient booking processes. Many ride-hailing applications also overwhelm users with complex interfaces and unnecessary steps. Key problems: difficulty booking rides quickly during urgent situations, limited visibility into driver location and arrival times, complicated ride-request and confirmation flows, lack of trust during the waiting and pickup stages, and inconsistent user experiences across different ride-booking platforms. The challenge was to design a ride-booking solution that makes requesting, tracking, and completing rides simple, reliable, and transparent.',

      research:
        'Research was conducted through competitor analysis, user interviews, and market observations. Research objectives: understand how users currently book rides, identify frustrations in existing transportation apps, analyse successful ride-hailing experiences, and discover opportunities to improve trust and convenience.',

      insights: [
        'Speed is critical — users expect to book a ride within seconds, especially during emergencies.',
        'Real-time tracking builds confidence — seeing driver location and ETA calms the wait.',
        'Transparent pricing matters — unexpected charges are a leading cause of dissatisfaction.',
        'Safety influences decision-making — driver info, ride details, and live tracking are essentials.',
        'Mobile-first is expected — most ride requests start on a phone while the user is on the move.',
      ],

      challenges: [
        'Simplifying the Ride Booking Process — letting users request rides quickly without navigating multiple screens.',
        'Creating Trust Throughout the Journey — transparency on drivers, routes, and ride status at every step.',
        'Managing Real-Time Information — presenting live tracking data clearly without overwhelming the screen.',
        'Supporting Multiple User Scenarios — accommodating first-time users, regular commuters, and occasional riders.',
        'Balancing Functionality with Simplicity — booking, tracking, payments, ride history, and profile all had to stay easy to use.',
        'Ensuring a Consistent User Experience — every stage of the ride journey had to feel connected and intuitive.',
      ],

      decisions: [
        'Streamlined ride request flow — minimal steps from open-the-app to a confirmed ride; pickup and destination flow optimised for speed.',
        'Real-time tracking as a core feature — live map tracking and arrival updates are first-class, not buried in a sub-screen.',
        'Clear driver information — driver name, photo, vehicle details, and live ride status stay highly visible across the journey.',
        'Simplified navigation — clean structure so ride history, profile settings, and trip information are reachable in two taps.',
        'Mobile-first design — interface optimised for one-handed interactions and quick decisions on the move.',
        'Consistent visual language — unified design system with reusable components, typography, colours, and interaction patterns.',
      ],

      process: [
        { step: 'Business Goals', body: 'Defined business objectives and the expectations of both riders and service providers — anchored around convenience, safety, and operational efficiency.' },
        { step: 'Research & Discovery', body: 'Analysed user behaviours, transportation challenges, and competitor experiences to identify pain points and opportunities.' },
        { step: 'Visual Exploration', body: 'Explored multiple visual directions and interface styles to land on a clean, trustworthy design language aligned with the brand.' },
        { step: 'User Flows', body: 'Mapped two anchor journeys end-to-end — Authentication (Splash → Registration/Login → Verification → Home) and Ride Booking (Launch → Pickup → Destination → Vehicle → Confirm → Driver Assignment → Track → Complete).' },
        { step: 'Wireframing', body: 'Low-fidelity wireframes established content hierarchy, layout, and interaction patterns before any hi-fi design work.' },
        { step: 'UI Design & Prototyping', body: 'Hi-fi screens delivered a seamless ride-booking experience. Interactive prototypes validated flows and refined usability.' },
        { step: 'Design System', body: 'Scalable system covering buttons, form fields, navigation, map elements, ride-status cards, and profile components — using warm neutral tones with accent colours for a modern, approachable feel.' },
      ],

      outcomes: [
        'Simplified ride booking and onboarding experience.',
        'Improved visibility through real-time ride tracking.',
        'Enhanced trust with transparent driver and ride information.',
        'Faster task completion through streamlined user flows.',
        'Consistent and scalable design system.',
        'Mobile-first experience optimised for daily commuters.',
      ],

      learnings: [
        'Users value convenience above everything — the entire experience is judged by how much friction it removes.',
        'Trust plays a major role — accurate driver, route, and pricing information is the product, not a feature.',
        'Simplicity encourages usage — fewer booking steps directly correlate with higher completion.',
        'Visibility reduces anxiety — live updates and tracking give users a sense of control during the wait.',
        'Personalisation improves experience — saved locations, ride history, and preferred payment methods compound into a noticeably smoother journey.',
      ],

      role: 'Lead Product Designer',
      duration: '8 weeks',
      team: 'Solo design with engineering review',
      platform: 'iOS & Android',

      figmaUrl: 'https://www.figma.com/design/XmZnEpGkjGbRmjuKRgBfmT/Rydigo---Drive-App?node-id=0-1&p=f&t=T6VKUjEZ0YnckSOW-0',
    }),
  },
  u9: {
    id: 'u9', slug: 'cvs',
     title: 'CVS — Contactless Verification System',
    tagline: 'A contactless check-in kiosk that turned 4-minute warehouse queues into 48-second scans.',
    industry: 'Warehouses & Shipping', year: 'Logistics · Pune',
    cover: '/project/cvs.png',
    ...baseSections({
      overview:
        'CVS is a contactless verification system designed for warehouse and logistics operators in Pune. It replaces slow, paper-based worker check-in with a scalable digital protocol built around identity verification, health confirmation, and shift slotting. The goal: reduce gate bottlenecks, make daily attendance traceable, and keep the experience accessible for a workforce with low digital literacy, all under real warehouse conditions (glare, low light, dust, gloves, high throughput).',

      problem:
        'Paper-based entry at warehouse gates was slow (roughly 4 minutes per worker), lacked traceability, and created long shift-start queues that impacted downstream operations. During the pandemic this became critical, health screening was informal, protocol compliance was inconsistent (~61%), and there was no reliable audit trail per worker. The challenge: design a contactless, kiosk-and-mobile verification system that could handle high throughput, work in punishing environments, and be used confidently by workers with varying levels of digital literacy.',

      research:
        'Persona-led, field-validated research grounded in the daily reality of warehouse shift workers. Anchored around Rajesh Kumar a 32-year-old shift worker whose primary concern is a reliable daily wage. Scenario mapping traced the full arrival-to-check-in protocol across six panels, from staggered SMS slots outside the gate to identity verification and health screening at the kiosk. Competitor and industry reviews were secondary; the primary source of truth was observation of actual shift arrivals and the failure modes of existing paper systems.',

      insights: [
        'Workers prioritise reliable daily wages — technology must be forgiving of errors and reversible.',
        'Icon-only navigation fails for low-literacy users; text + icon redundancy is essential.',
        'Night-mode and high-contrast UI significantly reduce scan failures in real warehouse light.',
        'Staggered SMS arrival slots dramatically reduce "gate anxiety" and social crowding.',
        'Large, full-screen feedback communicates status better than small icons — even at a glance from a distance.',
      ],

      challenges: [
        'Tech intimidation — the average user had never used a self-service kiosk before.',
        'High-glare, low-light environments — the interface had to survive both midday sun and 5 a.m. shifts.',
        'Low digital literacy across a diverse workforce — the flow had to be self-explanatory.',
        'High throughput — the system had to move workers through in under a minute without breaking accuracy.',
        'Health compliance under pandemic conditions — screening had to be reliable and auditable.',
        'Reversibility — every wrong tap needed a clear, blameless way out.',
      ],

      decisions: [
        'Progressive disclosure — one action per screen, so users are never asked to make two decisions at once.',
        'Text + icon redundancy — every control is labelled, never icon-only, to accommodate low-literacy workers.',
        '48px minimum touch targets — designed for gloved hands, wet fingers, and hurried taps.',
        'Full-screen colour-coded feedback (Fail-Safe) — success, failure, and retry states occupy the entire screen, so status is unmistakable from a distance.',
        'High-contrast, night-mode-first visual system — legible in glare and low light without a mode switch.',
        'Staggered SMS shift slots — spreads arrivals across time to prevent gate crowding and reduce anxiety at the entry point.',
      ],

      process: [
        { step: 'Research', body: 'Field research at active warehouse gates in Pune. Observed paper-based entry, timed real check-ins, and mapped where the flow broke down under pressure.' },
        { step: 'Persona', body: 'Built the anchor persona (Rajesh Kumar, 32, shift worker) capturing digital literacy, motivations, environment, and constraints.' },
        { step: 'Scenario', body: 'A six-panel Shift Arrival & Screening protocol mapping arrival, SMS slot, identity check, health confirmation, entry, and edge-case handling.' },
        { step: 'Storyboard', body: 'Frame-by-frame illustration of the worker\u2019s experience from bus stop to workstation \u2014 ensuring the flow felt human, not procedural.' },
        { step: 'User Journey', body: 'Five-phase journey map (Arrival → Wait → Verify → Screen → Enter) with emotional highs and lows tied to specific design opportunities.' },
        { step: 'Task Flow', body: 'Security check-in task flow \u2014 the shortest possible decision tree from token to gate open.' },
        { step: 'Information Architecture', body: 'System hierarchy defining kiosk vs. mobile responsibilities, admin surfaces, and audit-trail data flow.' },
        { step: 'Wireframes', body: 'Lo-fi mobile screens for shift-slot pickup, identity verification, and health screening \u2014 optimised for the smallest possible cognitive load per screen.' },
        { step: 'High-Fidelity Design', body: 'Hi-fi UI screens with the final high-contrast visual system, colour-coded fail-safe feedback, and 48px touch targets throughout.' },
        { step: 'Evaluation', body: 'On-site usability observation with real workers, timing check-ins and refining copy, contrast, and feedback states until the flow held up under real conditions.' },
      ],

      outcomes: [
        'Check-in time cut from ~4 minutes to 48 seconds — an 80% reduction.',
        'Protocol compliance improved from 61% to 94% after switching from paper to digital.',
        'Queue length reduced by 73% at peak shift-arrival windows.',
        'Full audit trail per worker per shift, replacing scattered paper registers.',
        'The system rolled out across multiple sites with the same interface and no retraining.',
      ],

      learnings: [
        'Large, full-screen feedback is dramatically superior to small status icons in noisy, distant environments.',
        'Staggered arrivals reduce "gate anxiety" more effectively than any UI improvement.',
        'For low-literacy audiences, redundancy (icon + label + colour) is not clutter — it is the design.',
        'Design for the environment, not the demo. High-contrast, high-touch targets and fail-safe feedback exist because the warehouse gate exists.',
        'The success metric of an operational product is time and compliance, not screens shipped.',
      ],

      role: 'UX Research & Design (Lead)',
      duration: '2 weeks',
      team: 'Solo design with operations & IT stakeholders',
      platform: 'Kiosk & Mobile',

       gallery: [
        '/project/inner/cvs/scenario.png',
        '/project/inner/cvs/Persona.png',
        '/project/inner/cvs/journey.png',
        '/project/inner/cvs/task-flow.png',
        '/project/inner/cvs/ia.jpg',
       
  [
    { src: '/project/inner/cvs/1.png',  maxWidth: 200 },
    { src: '/project/inner/cvs/2.png',  maxWidth: 200 },
    { src: '/project/inner/cvs/3.png',  maxWidth: 200 },
  ],

],

      figmaUrl: 'https://www.figma.com/design/RM28H7UklifzKlNIEtBFoE/Assignment---Axis?node-id=52-2&t=RCpeCdPDrRnNIrjt-0',

    }),
  },
  u10: {
    id: 'u10', slug: 'staylo',
    title: 'Staylo',
    tagline: 'Premium stays, curated.',
    industry: 'Travel', year: 'R&D',
    cover: '/project/staylo.png',
    ...baseSections({
      overview: 'A small, opinionated stay app for travellers who would rather choose from 30 great properties than 30,000 mediocre ones.',
      problem: 'OTA platforms optimise for inventory volume, not curatorial taste.',
      insights: [
        'Fewer options, better explained, outperform endless scroll.',
        'Trust signals matter more than discounts in premium travel.',
      ],
    }),
  },
  u11: {
    id: 'u11', slug: 'TTM',
    title: 'TTM',
    tagline: 'A smart decision in managing a distributed workforce.',
    industry: 'Enterprise', year: 'R&D',
    cover: '/project/ttm.png',
    ...baseSections({
      overview: 'ApnaFood elevates local home-style kitchens that get lost in mainstream delivery platforms.',
      problem: 'Home kitchens rely on word-of-mouth. Mainstream apps don\u2019t serve their economics.',
      insights: [
        'Home kitchen storytelling is the strongest conversion lever.',
        'Smaller batches need different inventory UX than restaurants.',
      ],
    }),
  },
  u12: {
    id: 'u12', slug: 'doc-connect',
    title: 'Doc Connect',
    tagline: 'A health-secure app connecting patients with verified doctors.',
    industry: 'Healthcare', year: 'R&D',
    cover: '/project/doc-connect.png',
    ...baseSections({
     overview:
        'Doc Connect is a digital healthcare platform that streamlines the complete patient journey — from discovering the right doctor to booking appointments, attending virtual consultations, and accessing medical records in one place. The product was designed to remove the friction patients typically experience across healthcare apps and to build enough trust to encourage adoption across age groups and digital-literacy levels. Every decision was anchored around clarity, transparency, and reducing patient anxiety at the moments that matter.',

      problem:
        'Healthcare products often struggle to balance clinical depth with everyday usability. Patients face complex interfaces, unclear doctor availability, insufficient profile information, uncertain consultation outcomes, and repeated data entry across modules. First-time users and elderly patients feel especially overwhelmed, while regular users want faster discovery, transparent pricing, and easier access to previous consultations. The challenge was to design a platform that simplifies healthcare workflows for patients without stripping out essential clinical information — and to do it in a way that scales across future healthcare modules.',

      research:
        'Research included secondary research across the healthcare ecosystem, competitor teardowns of Practo, Apollo 24|7 and Tata 1mg, review of user feedback on live healthcare apps, and behavioural analysis across age groups to understand how digital literacy shapes healthcare adoption. Research focus: user expectations from a digital healthcare platform, pain points in booking and managing appointments, trust factors influencing online consultations, accessibility for first-time and elderly users, and information architecture of leading healthcare applications. The research revealed patients value convenience but often feel overwhelmed by complex healthcare interfaces \u2014 they want a faster way to discover the right doctor, book without unnecessary steps, and access previous consultations in one place. Anxiety around booking was driven by unclear doctor availability, thin profile information, and uncertainty about outcomes, so transparent pricing, simple navigation, and timely reminders became explicit requirements.',


      insights: [
        'Users prioritise speed and simplicity over feature-rich interfaces.',
        'Trust is built through verified doctor profiles, ratings, and transparent information.',
        'Appointment management should require minimal effort \u2014 fewer taps, clearer states.',
        'Patients appreciate proactive communication through reminders and status updates.',
        'A clean, accessible interface directly increases confidence in healthcare decisions.',
      ],

      challenges: [
        'Simplifying complex healthcare workflows without losing essential clinical information.',
        'Building user trust for online consultations \u2014 patients need reassurance that the doctor, data, and process are real and secure.',
        'Organising large volumes of medical information (appointments, prescriptions, records, reports) in an intuitive way.',
        'Designing for users with varying levels of digital literacy \u2014 from first-time app users to elderly patients.',
        'Maintaining consistency across multiple modules while keeping the system scalable for future healthcare services.',
        'Serving multiple stakeholders \u2014 patients, doctors, and healthcare providers \u2014 in one coherent product.',
      ],

      decisions: [
        'Step-by-step appointment booking \u2014 the booking flow was broken into a clear, linear sequence so users never feel lost.',
        'Doctor search with smart filters \u2014 specialisation, experience, location, and consultation mode surface the right doctor faster.',
        'Trust-forward doctor profiles \u2014 essential information (verification, qualifications, experience, fees, mode) is visible upfront, before booking.',
        'Unified patient dashboard \u2014 appointments, prescriptions, and medical records live in one place so history is never more than a tap away.',
        'Clear visual hierarchy \u2014 typography, spacing, and colour weight are used to reduce cognitive load on every screen.',
        'Responsive and accessible interfaces \u2014 layouts, contrast, and tap targets tuned for a diverse user base and every device.',
        'Familiar interaction patterns \u2014 conventions are respected wherever possible to minimise the learning curve for first-time users.',
      ],

      process: [
        { step: 'Discover', body: 'Conducted market research and competitor analysis. Identified user pain points and business objectives across the healthcare journey.' },
        { step: 'Define', body: 'Created personas and user journeys. Prioritised the core problems affecting appointment booking and consultation experience.' },
        { step: 'Ideate', body: 'Explored multiple user flows and interaction patterns. Low-fidelity wireframes validated navigation and information hierarchy.' },
        { step: 'Design', body: 'Developed high-fidelity interfaces on a scalable design system \u2014 visual consistency, accessibility, and responsive layouts baked in.' },
        { step: 'Validate', body: 'Evaluated task completion flows, refined interactions based on usability observations and stakeholder feedback, and iterated until the journey felt effortless.' },
      ],

      outcomes: [
        'Reduced unnecessary navigation across the patient journey.',
        'Improved information discoverability for doctors, appointments, and records.',
        'Increased user confidence during appointment booking.',
        'A scalable design system that accelerates future healthcare modules without breaking consistency.',
        'A more intuitive, trustworthy, and efficient healthcare platform overall.',
      ],

      learnings: [
        'Healthcare products must prioritise clarity over feature density \u2014 less is more in a high-anxiety context.',
        'Trust is strengthened through transparency, consistency, and thoughtful interaction design, not marketing copy.',
        'Accessibility is essential when designing for a diverse user base \u2014 age, literacy, and device all vary widely.',
        'Continuous validation with users leads to more confident design decisions and shorter iteration loops.',
        'Building a scalable design system early accelerates product growth while preserving design quality.',
      ],

      role: 'Lead Product Designer',
      duration: '10 weeks',
      team: 'Design lead + Product + Engineering + Clinical review',
      platform: 'Mobile & Web',

           gallery: [

  [
    { src: '/project/inner/doc/1.png',  maxWidth: 200 },
    { src: '/project/inner/doc/2.png',  maxWidth: 200 },
    { src: '/project/inner/doc/4.png',  maxWidth: 200 },
  ],
  [
    { src: '/project/inner/doc/5.png',  maxWidth: 200 },
    { src: '/project/inner/doc/6.png',  maxWidth: 200 },
    { src: '/project/inner/doc/7.png',  maxWidth: 200 },
    { src: '/project/inner/doc/8.png',  maxWidth: 200 },
  ],
  [
    { src: '/project/inner/doc/9.png',  maxWidth: 200 },
    { src: '/project/inner/doc/10.png', maxWidth: 200 },
    { src: '/project/inner/doc/11.png', maxWidth: 200 },
    { src: '/project/inner/doc/12.png', maxWidth: 200 },
    { src: '/project/inner/doc/13.png', maxWidth: 200 },
  ],

],

      figmaUrl: 'https://www.figma.com/design/x2PxElIUzp6JVIC3LnDlQ7/Free-Doctor-app-ui--Community---2-?node-id=0-1&p=f&t=XifPFp6FwZSLWDd3-0',

      
    }),
  },
    u13: {
    id: 'u13', slug: 'PPM',
    title: 'PPM \u2014  Project Portfolio Management',
    tagline: 'Seeing the whole picture while managing every detail',
    industry: 'Enterprise PPM', year: 'Project Objects',
    cover: '/project/ppm.png',
    ...baseSections({
      overview: 'PPM is the operational backbone for enterprise project governance — covering portfolio creation, demand management, resource capacity planning, programme management, financial tracking, and risk management. I owned the end-to-end UI/UX across all modules, ensuring a consistent, accessible, and highly usable experience for PMOs, portfolio managers, and project leads.',
      problem: 'Organisations were managing project portfolios across fragmented tools — budgeting in spreadsheets, risks in email chains, resource allocation in separate systems — with no unified view of portfolio performance. PMOs needed a single platform that could handle the full lifecycle: capturing demand, prioritising initiatives, planning resources, tracking financials, managing risks, and reporting performance. The design challenge was significant: PPM spans multiple user personas with fundamentally different needs. A PMO leader needs portfolio-wide analytics; a resource manager needs capacity views by skill and team; a project manager needs task-level detail. Designing one system that works across all of these without confusion or compromise required deep UX thinking at every layer.',
      research: 'I engaged directly with enterprise customers — Capital Projects, New Product Development, and IT portfolio contexts — to understand their distinct workflows. Requirement workshops surfaced that demand intake was often the most chaotic part of the process, with no standardised scoring or prioritisation method. Resource capacity planning was consistently identified as a critical gap: most teams were operating blind to bottlenecks until they hit them.',
      insights: [
        'PMO leaders wanted portfolio health at a glance — a single score or indicator per initiative, not raw numbers.',
        'Demand managers needed a configurable intake form with automated routing, not a generic request form.',
        'Resource planners depended on heatmaps and timeline views to spot capacity conflicts across skills and teams.',
        'Financial managers needed multi-currency, multi-version budget tracking with clear variance indicators.',
        'Risk tracking was often neglected because it felt like admin — the design needed to make it fast and contextual.',
      ],
      process: [
        { step: 'Discovery', body: 'Multi-persona research across PMO, resource, and financial roles. Requirement documentation per module.' },
        { step: 'Architecture', body: 'Modular IA design allowing PMOs to configure which views and modules are surfaced per role.' },
        { step: 'Design system', body: 'Extended PPM-specific component set — Gantt timeline component, resource heatmap, financial variance table.' },
        { step: 'Prototyping', body: 'High-fidelity prototypes for demand intake flow, resource planning workspace, and portfolio optimiser.' },
        { step: 'Engineering handoff', body: 'Full annotated handoff per module, integration point documentation, responsive grid specifications.' },
        { step: 'Iterate', body: 'Usability tests per module, multiple rounds of fixes, WCAG audit across all views, sign-off process.' },
      ],
      decisions: [
        'Configurable Role-Based Workspaces \u2014 Each persona — PMO, Resource Manager, Project Lead — gets a role-based workspace showing only what is relevant to them. Configuration is admin-managed, not per-user chaos.',
        'Waterfall / Agile / Hybrid Flexibility \u2014 Designed the project workspace to adapt to the project management methodology in use. The same interface presents Gantt timelines for waterfall or sprint boards for agile, driven by a project-level setting.',
        'Contextual Risk Entry \u2014 Redesigned risk management as a slide-in panel accessible from any project view, making logging a risk a 30-second action rather than navigating to a separate module.',
        'Scenario Comparison UI \u2014 Built a side-by-side scenario comparison view for what-if modelling, allowing portfolio managers to evaluate resource and financial impact before committing to a plan.',
      ],
      challenges: [
        '8+ Modules, One Coherent System \u2014 PPM is one of the most complex solutions in the suite. Maintaining a consistent design language across Portfolio, Demand, Resource, Program, Project, Financial, Risk, and Integration modules required relentless design system discipline.',
        'Dense Data Displays \u2014 Financial tables with multi-currency and multi-version data, resource heatmaps with skill-level granularity — making dense data readable and accessible required careful typographic hierarchy and colour semantic choices.',
        'Integration UX \u2014 Designing the UX for SAP, Jira, and Azure DevOps integration touchpoints — where data flows bidirectionally — required communicating sync states, conflicts, and data provenance clearly without overwhelming users.',
      ],
      outcomes: [
        '8+ Modules designed end-to-end',
        'Major enterprise integrations with custom UX',
        'WCAG AA compliant across all modules',
        'Adoption - demand management module usability-tested and refined',
      ],
      learnings: [
        'Multi-module enterprise products demand a shared design system as a single source of truth — without it, inconsistency compounds rapidly.',
        'Dense data UX is a specialisation. Contrast ratios, typographic hierarchy, and colour semantics carry the entire user experience in data-heavy view',
        'Making risk entry fast and contextual dramatically improved the quality of risk data captured — UX friction directly affects data quality.',
        'Usability testing across the full module breadth was essential; each module surfaced unique issues that cross-module testing alone would have missed.',
      ],
      team: 'Solo design with engineering team review & PM', duration: '24 Months', platform:'Web'
    }),
  },

      u14: {
    id: 'u14', slug: 'CWM',
    title: 'CWM \u2014  Collaborative Work Management',
    tagline: 'The digital workspace where work actually happens',
    industry: 'Enterprise CWM', year: 'Uppwise',
    cover: '/project/cwm.png',
    ...baseSections({
      overview: 'While the other Uppwise products addressed executive-level strategy and portfolio governance, CWM was designed for the people who actually do the work — the business users, project teams, and remote workers who needed a modern, flexible workspace for daily planning and coordination. CWM was also designed as the bridge between Uppwise enterprise portfolio layer and the teams executing within it — connecting OKRs, portfolio goals, and strategic objectives down to the individual tasks and sprint items that teams managed every day.',
      problem: 'Business teams — especially remote ones — were managing their work through a fragmented mix of email threads, shared spreadsheets, messaging apps, and basic task lists. No single view existed of who was doing what, when, and whether it connected to any organisational priority. The result was duplicated effort, missed deadlines, and no visibility for managers into actual team capacity.',
      research: 'I conducted user research with remote and distributed teams — the primary user base for CWM. The dominant pain point was context switching: people moved between 6–8 tools daily to complete a single workday. A secondary pain point was visibility: team leads had no single view of who was working on what, what was blocked, and what was falling behind.',
      insights: [
        'Task assignments, status updates, and approvals all happened in email — a medium designed for correspondence, not work management. Users wanted email-free work coordination but were not sure what that looked like.',
        'Some users think in lists, others in boards, others in timelines. A flexible view system — not a single prescribed layout — was essential to adoption across diverse working styles.',
        'IT and product teams were comfortable with agile; finance, HR, and marketing teams were not. CWM needed to support agile ceremonies without alienating non-technical business users.',
        'Teams that could see how their daily tasks connected to OKRs and portfolio goals reported higher engagement. Making the "why" visible at the task level was a differentiating design opportunity.',
        
      ],
      process: [
        { step: 'Discovery', body: 'Business user interviews, remote team workflow analysis, tool audit (Slack, Trello, Monday), OKR connection research' },
        { step: 'Architecture', body: 'Workspace structure, task entity model, view type system (list / board / timeline / calendar)' },
        { step: 'Design system', body: 'Task card components, kanban board patterns, timeline bars, resource scheduling grid, scrum board UI' },
        { step: 'Prototyping', body: 'Workspace creation, task assignment flows, kanban drag interaction, scrum sprint management, OKR linking' },
        { step: 'Engineering handoff', body: 'Mobile-responsive specs, drag-and-drop interaction specs, real-time collaboration states, notification system' },
        { step: 'Iterate', body: 'Beta user testing, kanban UX refinements, mobile experience optimisation, onboarding flow improvements' },
      ],
      decisions: [
        'Configurable workspaces as the organising principle \u2014 Rather than a fixed product structure, designed workspaces as flexible containers — teams could organise work by project, by function, or by any combination that matched their working model.',
        'Multi-view as standard, not a premium feature \u2014 List, board, timeline, and calendar views were designed as equivalent first-class views — not add-ons. Switching between views was made frictionless so teams naturally found their preferred working style.',
        'OKR linking at the task level \u2014 Designed an explicit connection model so individual tasks could be linked to team OKRs, which connected upward to portfolio goals and strategic themes. This made the "why" visible from the task itself.',
        'Resource scheduling as a visual, drag-based experience \u2014 Moved resource assignment from a form-based dropdown experience to a visual scheduling grid where managers could see team capacity and reassign work by dragging — mirroring the mental model of managing a team calendar.',
      ],
      challenges: [
        'Competing with consumer-grade tools in a B2B context \u2014 Users compared CWM to Notion, Trello, and Monday. Designing an experience that felt as polished and immediately usable as consumer tools while supporting enterprise-grade complexity and data volumes was a significant design challenge.',
        'Real-time collaboration design complexity \u2014 Multiple users editing the same board, assigning the same resource, or updating the same task simultaneously required careful design of conflict states, presence indicators, and optimistic update patterns.',
        'Mobile-first for a data-rich product \u2014 CWM needed to work well on mobile for remote workers — but the data density of kanban boards, timelines, and resource grids was inherently hostile to small screens. Designed mobile-specific views rather than responsive downsizing of desktop layouts.',
      ],
      outcomes: [
        'Workspace replacing email, spreadsheets, and messaging for work coordination',
        'List, board, timeline, calendar — every working style served',
        'Individual tasks linked to OKRs and portfolio strategy',
      ],
      learnings: [
        'Enterprise software has historically been functional but joyless. Investing design effort in micro-interactions, transitions, and empty states created measurably stronger user sentiment in qualitative testing.',
        'CWM blank workspace was intimidating without guidance. Designing opinionated starter templates and contextual onboarding prompts dramatically reduced time-to-first-value for new teams.',
        'Responsive scaling does not work for dense data products. Designing mobile-native views for CWM — with different information hierarchies and interaction patterns than desktop — was the right call and significantly improved mobile retention.',
        'Users now expect enterprise tools to feel as polished as the consumer apps they use daily. Raising the UX quality bar for CWM raised expectations across the entire Uppwise product suite — a positive feedback loop.',       
      ],
      team: 'Me, Junior Designer, CTO ', duration: '18 Months', platform:'Web'
    }),
  },
};

export const caseStudyBySlug = (slug) =>
  Object.values(caseStudies).find((c) => c.slug === slug);
